import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Container, Typography, Paper, Stack, Grid,
  FormControl, InputLabel, Select, MenuItem, TextField,
  Button, Card, CardContent, Avatar, Chip, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, Divider, useTheme, alpha,
  Dialog, DialogTitle, DialogContent, DialogActions,
  SelectChangeEvent, Backdrop, CircularProgress, Snackbar,
  ToggleButton, ToggleButtonGroup,
  InputAdornment, Badge
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  LocalBar as BarIcon,
  Restaurant as RestaurantIcon,
  AccessTime as ShiftIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  ArrowBack as BackIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  DateRange as DateRangeIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useHistory } from 'react-router-dom';

// Ionic Imports
import { IonContent, IonPage } from '@ionic/react';
import { baseUrl, config, requestID } from "../../../common/utils";

// Types
interface Waiter {
  user_id: string;
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  status: string;
  roleID: number;
}

interface Assignment {
  id: number;
  assignmentDate: string;
  waiterId: string;
  waiterName?: string;
  supervisorId: string;
  supervisorName?: string;
  status: 'assigned' | 'completed' | 'cancelled';
  department: 'bar' | 'restaurant';
  shift?: string;
  notes?: string;
}

interface FilterOptions {
  date: Date | null;
  department: 'all' | 'bar' | 'restaurant';
  status: 'all' | 'assigned' | 'completed' | 'cancelled';
  search: string;
}

const DEPARTMENTS = [
  { value: 'bar', label: 'Bar', icon: <BarIcon />, color: '#3b82f6' },
  { value: 'restaurant', label: 'Restaurant', icon: <RestaurantIcon />, color: '#f97316' }
];

const SHIFTS = [
  { value: 'morning', label: 'Morning (6AM - 2PM)' },
  { value: 'afternoon', label: 'Afternoon (2PM - 10PM)' },
  { value: 'evening', label: 'Evening (10PM - 6AM)' },
  { value: 'full', label: 'Full Day' }
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'assigned', label: 'Assigned', color: '#10b981' },
  { value: 'completed', label: 'Completed', color: '#3b82f6' },
  { value: 'cancelled', label: 'Cancelled', color: '#ef4444' }
];

const WaiterAssignment: React.FC = () => {
  const theme = useTheme();
  const history = useHistory();
  const [waiters, setWaiters] = useState<Waiter[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [filteredAssignments, setFilteredAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [showFilters, setShowFilters] = useState(true);
  
  // Form states for new assignment
  const [selectedWaiter, setSelectedWaiter] = useState<string>('');
  const [selectedDepartment, setSelectedDepartment] = useState<'bar' | 'restaurant'>('bar');
  const [selectedShift, setSelectedShift] = useState<string>('morning');
  const [notes, setNotes] = useState<string>('');
  
  // Filter states
  const [filters, setFilters] = useState<FilterOptions>({
    date: new Date(),
    department: 'all',
    status: 'all',
    search: ''
  });
  
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch waiters - This is crucial for the dropdown
  const fetchWaiters = async () => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(`${baseUrl()}BeverageSupervisors/getWaiters`, formData, config);
      
      if (response.data.status) {
        console.log("Waiters fetched:", response.data.data); // Debug log
        setWaiters(response.data.data || []);
      } else {
        showNotification('Failed to fetch waiters', 'error');
      }
    } catch (error) {
      console.error("Fetch waiters error:", error);
      showNotification('Error fetching waiters', 'error');
    }
  };

  // Format date to YYYY-MM-DD
  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Fetch assignments by single date
  const fetchAssignmentsByDate = async () => {
    if (!filters.date) return;
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("assignmentDate", formatDate(filters.date));

      const response = await axios.post(`${baseUrl()}BeverageSupervisors/getAssignmentsByDate`, formData, config);
      
      if (response.data.status) {
        const mappedAssignments = (response.data.data || []).map((item: any) => ({
          id: item.id,
          assignmentDate: item.assignmentDate,
          waiterId: item.waiterId,
          waiterName: item.waiterName,
          supervisorId: item.supervisorId,
          supervisorName: item.supervisorName,
          department: item.department,
          status: item.status,
          shift: item.shift || 'morning',
          notes: item.notes || ''
        }));
        setAssignments(mappedAssignments);
        applyFilters(mappedAssignments);
      } else {
        setAssignments([]);
        setFilteredAssignments([]);
      }
    } catch (error) {
      console.error("Fetch assignments error:", error);
      showNotification('Failed to fetch assignments', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Apply filters locally
  const applyFilters = (data: Assignment[]) => {
    let filtered = [...data];

    if (filters.department !== 'all') {
      filtered = filtered.filter(a => a.department === filters.department);
    }

    if (filters.status !== 'all') {
      filtered = filtered.filter(a => a.status === filters.status);
    }

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(a => 
        a.waiterName?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredAssignments(filtered);
  };

  // Handle filter changes
  const handleFilterChange = (field: keyof FilterOptions, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      const localDate = new Date(date);
      localDate.setHours(12, 0, 0, 0);
      handleFilterChange('date', localDate);
    } else {
      handleFilterChange('date', null);
    }
  };

  const clearFilters = () => {
    setFilters({
      date: new Date(),
      department: 'all',
      status: 'all',
      search: ''
    });
  };

  // Handle new assignment
  const handleAssign = async () => {
    if (!selectedWaiter || !selectedDepartment) {
      showNotification('Please select waiter and department', 'warning');
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      
      const today = new Date();
      const formattedDate = formatDate(today);
      
      formData.append("assignmentDate", formattedDate);
      formData.append("waiterId", selectedWaiter);
      formData.append("department", selectedDepartment);
      formData.append("shift", selectedShift);
      formData.append("notes", notes);
      formData.append("status", "assigned");

      const response = await axios.post(`${baseUrl()}BeverageSupervisors/assignWaiter`, formData, config);
      
      if (response.data.status) {
        showNotification('Waiter assigned successfully', 'success');
        resetForm();
        await fetchAssignmentsByDate();
      } else {
        showNotification(response.data.message || 'Failed to assign waiter', 'error');
      }
    } catch (error) {
      console.error("Assign waiter error:", error);
      showNotification('Error assigning waiter', 'error');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setSelectedWaiter('');
    setSelectedDepartment('bar');
    setSelectedShift('morning');
    setNotes('');
  };

  const getWaiterName = (waiterId: string) => {
    const waiter = waiters.find(w => w.user_id === waiterId);
    return waiter?.fullName || 'Unknown';
  };

  // Apply filters when filter criteria change
  useEffect(() => {
    applyFilters(assignments);
  }, [filters.department, filters.status, filters.search, assignments]);

  // Fetch assignments when date changes
  useEffect(() => {
    if (waiters.length > 0 && filters.date) {
      fetchAssignmentsByDate();
    }
  }, [filters.date, waiters]);

  // Initial load
  useEffect(() => {
    fetchWaiters();
  }, []);

  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'assigned': return '#10b981';
      case 'completed': return '#3b82f6';
      case 'cancelled': return '#ef4444';
      default: return theme.palette.grey[500];
    }
  };

  const getDateLabel = () => {
    if (filters.date) {
      const month = filters.date.toLocaleString('default', { month: 'long' });
      const day = filters.date.getDate();
      const year = filters.date.getFullYear();
      return `Showing assignments for ${month} ${day}, ${year}`;
    }
    return "Select a date to view assignments";
  };

  const activeFilterCount = Object.values(filters).filter(v => v && v !== 'all' && v !== '').length;

  return (
    <IonPage>
      <IonContent>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh' }}>
            
            {/* Header */}
            <Paper 
              elevation={0}
              sx={{ 
                borderBottom: `1px solid ${theme.palette.divider}`,
                position: 'sticky',
                top: 0,
                zIndex: 10,
                bgcolor: 'background.paper'
              }}
            >
              <Container maxWidth="xl">
                <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 2 }}>
                  <IconButton onClick={() => history.goBack()}>
                    <BackIcon />
                  </IconButton>
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      color: theme.palette.success.main
                    }}
                  >
                    <AssignmentIcon />
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="h5" fontWeight={600}>
                      Waiter Assignments
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {getDateLabel()}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={fetchAssignmentsByDate}
                    size="small"
                  >
                    Refresh
                  </Button>
                </Stack>
              </Container>
            </Paper>

            <Container maxWidth="xl" sx={{ py: 3 }}>
              
              {/* Filters Section */}
              <Paper 
                elevation={0}
                sx={{ 
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  mb: 3,
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ p: 2, bgcolor: theme.palette.background.paper }}>
                  <Stack 
                    direction="row" 
                    justifyContent="space-between" 
                    alignItems="center"
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Badge 
                        badgeContent={activeFilterCount} 
                        color="primary"
                        invisible={activeFilterCount === 0}
                      >
                        <FilterIcon color="action" />
                      </Badge>
                      <Typography variant="subtitle1" fontWeight={600}>
                        Filters
                      </Typography>
                    </Stack>
                    <Button
                      size="small"
                      onClick={() => setShowFilters(!showFilters)}
                      endIcon={<FilterIcon />}
                    >
                      {showFilters ? 'Hide Filters' : 'Show Filters'}
                    </Button>
                  </Stack>
                </Box>

                {showFilters && (
                  <>
                    <Divider />
                    <Box sx={{ p: 2 }}>
                      <Grid container spacing={2}>
                        {/* Date Filter */}
                        <Grid item xs={12} md={3}>
                          <DatePicker
                            label="Select Date"
                            value={filters.date}
                            onChange={handleDateChange}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: 'small',
                                InputProps: {
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <DateRangeIcon fontSize="small" />
                                    </InputAdornment>
                                  ),
                                },
                              },
                            }}
                          />
                        </Grid>
                        
                        {/* Department Filter */}
                        <Grid item xs={12} md={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Department</InputLabel>
                            <Select
                              value={filters.department}
                              label="Department"
                              onChange={(e) => handleFilterChange('department', e.target.value)}
                            >
                              <MenuItem value="all">All Departments</MenuItem>
                              {DEPARTMENTS.map(dept => (
                                <MenuItem key={dept.value} value={dept.value}>
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <Box sx={{ color: dept.color }}>{dept.icon}</Box>
                                    <span>{dept.label}</span>
                                  </Stack>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>

                        {/* Status Filter */}
                        <Grid item xs={12} md={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                              value={filters.status}
                              label="Status"
                              onChange={(e) => handleFilterChange('status', e.target.value)}
                            >
                              {STATUS_OPTIONS.map(option => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>

                        {/* Search Filter */}
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Search waiter"
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <PersonIcon fontSize="small" />
                                </InputAdornment>
                              ),
                              endAdornment: filters.search && (
                                <InputAdornment position="end">
                                  <IconButton size="small" onClick={() => handleFilterChange('search', '')}>
                                    <ClearIcon fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                      </Grid>
                      
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          {filteredAssignments.length} assignment(s) found
                        </Typography>
                        <Button 
                          variant="text" 
                          size="small" 
                          onClick={clearFilters}
                          startIcon={<ClearIcon />}
                        >
                          Clear All Filters
                        </Button>
                      </Stack>
                    </Box>
                  </>
                )}
              </Paper>

              <Grid container spacing={3}>
                
                {/* Assignment Form - WITH WAITER DROPDOWN */}
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      New Assignment
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    
                    <Stack spacing={3}>
                      {/* Waiter Dropdown - This is where waiters are selected */}
                      <FormControl fullWidth size="small">
                        <InputLabel>Select Waiter</InputLabel>
                        <Select
                          value={selectedWaiter}
                          onChange={(e) => setSelectedWaiter(e.target.value)}
                          label="Select Waiter"
                        >
                          {waiters.length === 0 ? (
                            <MenuItem disabled>No waiters available</MenuItem>
                          ) : (
                            waiters.map(waiter => (
                              <MenuItem key={waiter.user_id} value={waiter.user_id}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Avatar sx={{ width: 24, height: 24, fontSize: 14 }}>
                                    {waiter.fullName?.charAt(0) || '?'}
                                  </Avatar>
                                  <span>{waiter.fullName}</span>
                                </Stack>
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>

                      {/* Department Toggle */}
                      <ToggleButtonGroup
                        value={selectedDepartment}
                        exclusive
                        onChange={(e, value) => value && setSelectedDepartment(value)}
                        fullWidth
                      >
                        {DEPARTMENTS.map(dept => (
                          <ToggleButton value={dept.value} key={dept.value}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Box sx={{ color: dept.color }}>{dept.icon}</Box>
                              <span>{dept.label}</span>
                            </Stack>
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>

                      {/* Shift Selection */}
                      <FormControl fullWidth size="small">
                        <InputLabel>Shift</InputLabel>
                        <Select
                          value={selectedShift}
                          onChange={(e) => setSelectedShift(e.target.value)}
                          label="Shift"
                        >
                          {SHIFTS.map(shift => (
                            <MenuItem key={shift.value} value={shift.value}>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <ShiftIcon fontSize="small" />
                                <span>{shift.label}</span>
                              </Stack>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {/* Notes */}
                      <TextField
                        label="Notes (Optional)"
                        multiline
                        rows={3}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        size="small"
                        placeholder="Add any special instructions..."
                      />

                      {/* Submit Button */}
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleAssign}
                        disabled={!selectedWaiter || saving}
                        fullWidth
                      >
                        {saving ? 'Assigning...' : 'Assign Waiter'}
                      </Button>
                    </Stack>
                  </Paper>
                </Grid>

                {/* Assignments List */}
                <Grid item xs={12} md={8}>
                  <Paper sx={{ p: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Typography variant="h6" fontWeight={600}>
                        Assignments ({filteredAssignments.length})
                      </Typography>
                      {filters.date && (
                        <Chip 
                          label={formatDate(filters.date)}
                          icon={<DateRangeIcon />}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      )}
                    </Stack>
                    <Divider sx={{ mb: 3 }} />

                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Waiter</TableCell>
                            <TableCell>Department</TableCell>
                            <TableCell>Shift</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Supervisor</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {loading ? (
                            <TableRow>
                              <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                <CircularProgress size={32} />
                              </TableCell>
                            </TableRow>
                          ) : filteredAssignments.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                <Stack spacing={2} alignItems="center">
                                  <AssignmentIcon sx={{ fontSize: 48, color: theme.palette.grey[300] }} />
                                  <Typography color="text.secondary">
                                    No assignments found for {filters.date ? filters.date.toLocaleDateString() : 'selected date'}
                                  </Typography>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ) : (
                            filteredAssignments.map((assignment) => (
                              <TableRow key={assignment.id} hover>
                                <TableCell>
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
                                      {(assignment.waiterName || '?').charAt(0)}
                                    </Avatar>
                                    <Typography variant="body2">
                                      {assignment.waiterName || 'Unknown'}
                                    </Typography>
                                  </Stack>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    icon={assignment.department === 'bar' ? <BarIcon /> : <RestaurantIcon />}
                                    label={assignment.department === 'bar' ? 'Bar' : 'Restaurant'}
                                    size="small"
                                    sx={{
                                      bgcolor: alpha(
                                        assignment.department === 'bar' ? '#3b82f6' : '#f97316', 
                                        0.1
                                      ),
                                      color: assignment.department === 'bar' ? '#3b82f6' : '#f97316'
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    icon={<ShiftIcon />}
                                    label={assignment.shift || 'morning'}
                                    size="small"
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={assignment.status}
                                    size="small"
                                    sx={{
                                      bgcolor: alpha(getStatusColor(assignment.status), 0.1),
                                      color: getStatusColor(assignment.status)
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {assignment.supervisorName || 'Unknown'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* Summary */}
                    {filteredAssignments.length > 0 && (
                      <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Summary
                        </Typography>
                        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                          {DEPARTMENTS.map(dept => {
                            const count = filteredAssignments.filter(a => a.department === dept.value).length;
                            return (
                              <Chip
                                key={dept.value}
                                icon={dept.icon}
                                label={`${count} in ${dept.label}`}
                                sx={{
                                  bgcolor: alpha(dept.color, 0.1),
                                  color: dept.color,
                                  mb: 1
                                }}
                              />
                            );
                          })}
                        </Stack>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </Container>

            {/* Snackbar */}
            <Snackbar
              open={snackbar.open}
              autoHideDuration={4000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
                {snackbar.message}
              </Alert>
            </Snackbar>

            {/* Loading Overlay */}
            <Backdrop
              sx={{
                color: '#fff',
                zIndex: theme.zIndex.drawer + 1,
                backdropFilter: 'blur(3px)'
              }}
              open={loading && filteredAssignments.length === 0}
            >
              <Stack spacing={2} alignItems="center">
                <CircularProgress color="inherit" />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Loading assignments...
                </Typography>
              </Stack>
            </Backdrop>

          </Box>
        </LocalizationProvider>
      </IonContent>
    </IonPage>
  );
};

export default WaiterAssignment;